import { useState, useEffect, useMemo, useRef } from 'react'
import { X, ChevronDown, Check, Star } from 'lucide-react'
import { T } from '../theme'
import playerService from '../services/playerService'
import ShareWhatsAppModal from './ShareWhatsAppModal'

/**
 * SendCardsModal v5 — envoi des cartes joueurs pour un match.
 *
 * POST /api/matches/{matchId}/send-all-cards/ accepte par joueur :
 * coach_rating (1-10|null, PRIVE), identity_tag (PUBLIC), goals/assists/
 * minutes_played (carte V2, f2) ; + private_message global + un seul
 * homme_du_match_player_id. Succes -> ShareWhatsAppModal (flow PR #20).
 *
 * Props :
 *  - isOpen, onClose
 *  - matchId
 *  - matchInfo : { scoreUs, scoreThem, opponentName, date }
 *      scoreUs/scoreThem sont ORIENTES CLUB par le parent, jamais
 *      score_home/score_away bruts (cf. src/utils/matchResult.js).
 *  - lineup : Player[] resolus { id, name, position, number, email, phone, photo_url }
 *  - onSuccess(response) : le parent affiche le toast
 *  - onPlayerUpdated(id, patch) : remonte un email/phone ajoute inline
 */

const API = import.meta.env.VITE_API_URL || 'https://backend-pued.onrender.com/api'

const D     = '#0a0908'
const PANEL = '#1a1916'
const GOLD  = '#4F8EF7'
const GOLDB = '#f5d76e'
const CREAM = '#f5f2eb'
const MUTED = 'rgba(245,242,235,0.5)'
const RULE  = 'rgba(245,242,235,0.12)'
const RED   = '#ef4444'
const MONO  = T.mono
const DISP  = T.display
const BODY  = T.body

const PREDEFINED_TAGS = [
  { label: 'Guerrier', emoji: '⚔️' },
  { label: 'Mur', emoji: '🛡️' },
  { label: 'Patron', emoji: '👑' },
  { label: 'Renard', emoji: '🦊' },
  { label: 'Sniper', emoji: '🎯' },
  { label: 'Maestro', emoji: '🎼' },
  { label: 'Métronome', emoji: '⚙️' },
  { label: 'Foudre', emoji: '⚡' },
  { label: 'Roc', emoji: '🗿' },
  { label: 'Pieuvre', emoji: '🐙' },
  { label: 'Coffre-fort', emoji: '🔒' },
  { label: 'Étincelle', emoji: '✨' },
  { label: 'Soldat', emoji: '🪖' },
  { label: 'Phénix', emoji: '🔥' },
  { label: 'Révélation', emoji: '💎' },
  { label: 'Capitaine', emoji: '🎖️' },
]
const CUSTOM_TAG_EMOJIS = ['⚔️', '🛡️', '👑', '🎯', '⚡']

function authHeaders() {
  const token = localStorage.getItem('insightball_token')
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
}

async function safeJson(res) {
  try { return await res.json() } catch { return null }
}

function initials(name) {
  return (name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('')
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim())
}

function isValidPhone(phone) {
  return /^0[1-9]\d{8}$/.test((phone || '').replace(/[\s.]/g, ''))
}

// Borne un input stat ('' -> null, sinon entier clampe).
function parseStat(raw, max) {
  if (raw == null || String(raw).trim() === '') return null
  let n = parseInt(String(raw).replace(/[^\d]/g, ''), 10)
  if (Number.isNaN(n)) return null
  if (n < 0) n = 0
  if (n > max) n = max
  return n
}

function PlayerAvatar({ photoUrl, name, size = 36 }) {
  const [broken, setBroken] = useState(false)
  if (photoUrl && !broken) {
    return (
      <img
        src={photoUrl}
        alt=""
        onError={() => setBroken(true)}
        style={{ width: size, height: size, objectFit: 'cover', flexShrink: 0, borderRadius: 4, border: `1px solid ${RULE}` }}
      />
    )
  }
  return (
    <div style={{
      width: size, height: size, flexShrink: 0, borderRadius: 4,
      background: GOLD, color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: MONO, fontSize: size > 44 ? 16 : 12, fontWeight: 700,
    }}>
      {initials(name)}
    </div>
  )
}

function CheckBox({ checked, hasContact, onChange }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={onChange}
      style={{
        width: 24, height: 24, flexShrink: 0, padding: 0, cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: checked ? GOLD : 'transparent',
        border: `2px solid ${checked ? GOLD : (hasContact ? GOLD : 'rgba(245,242,235,0.35)')}`,
      }}
    >
      {checked && <Check size={15} color={D} strokeWidth={3} />}
    </button>
  )
}

function EmailEdit({ initial, saving, error, onSave, onCancel }) {
  const [draft, setDraft] = useState(initial || '')
  const ref = useRef(null)
  useEffect(() => {
    function onDown(e) { if (ref.current && !ref.current.contains(e.target)) onCancel() }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [onCancel])
  const valid = isValidEmail(draft)
  return (
    <div ref={ref} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <input
        type="email" autoFocus value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && valid && !saving) onSave(draft.trim())
          if (e.key === 'Escape') onCancel()
        }}
        placeholder="email@exemple.fr"
        style={{ background: D, border: `1px solid ${GOLD}`, color: CREAM, fontFamily: MONO, fontSize: 11, padding: '4px 8px', borderRadius: 2, width: 200, maxWidth: '100%' }}
      />
      <button type="button" onClick={() => { if (valid && !saving) onSave(draft.trim()) }} disabled={!valid || saving} title="Enregistrer"
        style={{ background: 'transparent', border: `1px solid ${RULE}`, padding: 4, cursor: (!valid || saving) ? 'not-allowed' : 'pointer' }}>
        <Check size={14} color={valid ? GOLD : MUTED} />
      </button>
      <button type="button" onClick={onCancel} title="Annuler" style={{ background: 'transparent', border: `1px solid ${RULE}`, padding: 4, cursor: 'pointer' }}>
        <X size={14} color={MUTED} />
      </button>
      {saving && <span style={{ fontFamily: MONO, fontSize: 9, color: MUTED }}>...</span>}
      {error && <span style={{ fontFamily: MONO, fontSize: 9, color: RED }}>{error}</span>}
    </div>
  )
}

function PhoneEdit({ initial, saving, error, onSave, onCancel }) {
  const [draft, setDraft] = useState(initial || '')
  const ref = useRef(null)
  useEffect(() => {
    function onDown(e) { if (ref.current && !ref.current.contains(e.target)) onCancel() }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [onCancel])
  const valid = isValidPhone(draft)
  return (
    <div ref={ref} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <input
        type="tel" inputMode="numeric" autoFocus value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && valid && !saving) onSave(draft.replace(/[\s.]/g, ''))
          if (e.key === 'Escape') onCancel()
        }}
        placeholder="06 12 34 56 78"
        style={{ background: D, border: `1px solid ${GOLD}`, color: CREAM, fontFamily: MONO, fontSize: 11, padding: '4px 8px', borderRadius: 2, width: 170, maxWidth: '100%' }}
      />
      <button type="button" onClick={() => { if (valid && !saving) onSave(draft.replace(/[\s.]/g, '')) }} disabled={!valid || saving} title="Enregistrer"
        style={{ background: 'transparent', border: `1px solid ${RULE}`, padding: 4, cursor: (!valid || saving) ? 'not-allowed' : 'pointer' }}>
        <Check size={14} color={valid ? GOLD : MUTED} />
      </button>
      <button type="button" onClick={onCancel} title="Annuler" style={{ background: 'transparent', border: `1px solid ${RULE}`, padding: 4, cursor: 'pointer' }}>
        <X size={14} color={MUTED} />
      </button>
      {saving && <span style={{ fontFamily: MONO, fontSize: 9, color: MUTED }}>...</span>}
      {error && <span style={{ fontFamily: MONO, fontSize: 9, color: RED }}>{error}</span>}
    </div>
  )
}

function TagPicker({ value, onChange, compact }) {
  const [open, setOpen] = useState(false)
  const [customMode, setCustomMode] = useState(false)
  const [customLabel, setCustomLabel] = useState('')
  const [customEmoji, setCustomEmoji] = useState(CUSTOM_TAG_EMOJIS[0])
  const pickerRef = useRef(null)

  useEffect(() => {
    if (!open) return
    function handleClickOutside(e) {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) { setOpen(false); setCustomMode(false) }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const reset = () => { setOpen(false); setCustomMode(false); setCustomLabel(''); setCustomEmoji(CUSTOM_TAG_EMOJIS[0]) }
  const customValid = customLabel.trim().length >= 1 && customLabel.trim().length <= 18 && CUSTOM_TAG_EMOJIS.includes(customEmoji)

  const btnBase = { width: '100%', textAlign: 'left', padding: '8px 10px', background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: MONO, fontSize: 12, color: CREAM }

  return (
    <div ref={pickerRef} style={{ position: 'relative', minWidth: 0 }}>
      <button
        type="button"
        onClick={() => { if (open) reset(); else setOpen(true) }}
        title={value ? `${value.emoji} ${value.label}` : 'Choisir un tag'}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
          width: '100%', padding: compact ? '6px 8px' : '7px 10px',
          background: value ? 'rgba(79,142,247,0.12)' : 'transparent',
          border: `1px ${value ? 'solid' : 'dashed'} ${value ? GOLD : RULE}`,
          fontFamily: MONO, fontSize: compact ? 11 : 12, color: value ? GOLDB : MUTED, cursor: 'pointer',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value ? `${value.emoji} ${value.label}` : '+ Tag'}
        </span>
        <ChevronDown size={13} color={value ? GOLD : MUTED} style={{ flexShrink: 0 }} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', right: 0,
          width: '100%', minWidth: 220, zIndex: 60,
          background: PANEL, border: `1px solid rgba(79,142,247,0.4)`,
          maxHeight: 320, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
        }}>
          {!customMode ? (
            <>
              {PREDEFINED_TAGS.map(tag => (
                <button key={tag.label} type="button" onClick={() => { onChange(tag); reset() }} style={btnBase}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(79,142,247,0.1)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  {tag.emoji} {tag.label}
                </button>
              ))}
              <div style={{ borderTop: `1px solid ${RULE}` }} />
              <button type="button" onClick={() => setCustomMode(true)} style={{ ...btnBase, color: GOLD }}>✏️ Autre...</button>
              {value && (
                <button type="button" onClick={() => { onChange(null); reset() }} style={{ ...btnBase, color: RED, borderTop: `1px solid ${RULE}` }}>
                  ✕ Retirer le tag
                </button>
              )}
            </>
          ) : (
            <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <label style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', color: MUTED }}>Nom du tag</label>
              <input type="text" value={customLabel} onChange={e => setCustomLabel(e.target.value.slice(0, 18))} maxLength={18} autoFocus placeholder="Ton tag"
                style={{ width: '100%', padding: '8px 10px', background: 'rgba(245,242,235,0.04)', border: `1px solid ${RULE}`, fontFamily: MONO, fontSize: 13, color: CREAM }} />
              <span style={{ fontFamily: MONO, fontSize: 9, color: MUTED, alignSelf: 'flex-end' }}>{customLabel.length}/18</span>
              <label style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', color: MUTED }}>Emoji</label>
              <div style={{ display: 'flex', gap: 6 }}>
                {CUSTOM_TAG_EMOJIS.map(em => (
                  <button key={em} type="button" onClick={() => setCustomEmoji(em)}
                    style={{ width: 38, height: 38, cursor: 'pointer', background: customEmoji === em ? 'rgba(79,142,247,0.18)' : 'transparent', border: `1px solid ${customEmoji === em ? GOLD : RULE}`, fontSize: 18 }}>
                    {em}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button type="button" onClick={() => setCustomMode(false)}
                  style={{ flex: 1, padding: '8px', cursor: 'pointer', background: 'transparent', border: `1px solid ${RULE}`, fontFamily: MONO, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', color: MUTED }}>
                  ← Retour
                </button>
                <button type="button" disabled={!customValid} onClick={() => { onChange({ label: customLabel.trim(), emoji: customEmoji }); reset() }}
                  style={{ flex: 1, padding: '8px', cursor: customValid ? 'pointer' : 'not-allowed', background: customValid ? GOLD : 'rgba(79,142,247,0.25)', color: customValid ? D : MUTED, border: 'none', fontFamily: MONO, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', fontWeight: 700 }}>
                  Valider
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Slider note coach 0-10. 0 = "Non défini" (envoye null au backend, qui
// exige 1-10). Valeur en or des qu'elle est > 0.
function RatingSlider({ value, onChange, large }) {
  const v = value ?? 0
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <input
        type="range" min={0} max={10} step={1} value={v}
        onChange={e => onChange(parseInt(e.target.value, 10))}
        style={{ flex: 1, accentColor: GOLD, cursor: 'pointer', opacity: v === 0 ? 0.5 : 1, minWidth: 0 }}
      />
      <div style={{
        flexShrink: 0, minWidth: large ? 70 : 52, textAlign: 'right',
        fontFamily: DISP, fontSize: large ? 22 : 15, lineHeight: 1,
        color: v === 0 ? MUTED : GOLDB,
      }}>
        {v === 0
          ? <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.06em', textTransform: 'uppercase' }}>Non défini</span>
          : <>{v}<span style={{ fontSize: large ? 12 : 9, color: GOLD }}>/10</span></>}
      </div>
    </div>
  )
}

function StatInput({ icon, label, value, onChange, max, large }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, minWidth: 0 }}>
      <input
        type="text" inputMode="numeric" value={value ?? ''}
        onChange={e => onChange(e.target.value.replace(/[^\d]/g, '').slice(0, 3))}
        placeholder="–" aria-label={label}
        style={{
          width: large ? '100%' : 38, height: large ? 40 : 34, textAlign: 'center',
          background: 'rgba(245,242,235,0.04)', border: `1px solid ${RULE}`,
          fontFamily: MONO, fontSize: large ? 16 : 13, color: CREAM, outline: 'none',
        }}
        onFocus={e => e.currentTarget.style.borderColor = GOLD}
        onBlur={e => {
          e.currentTarget.style.borderColor = RULE
          const c = parseStat(e.currentTarget.value, max)
          onChange(c == null ? '' : String(c))
        }}
      />
      <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.08em', textTransform: 'uppercase', color: MUTED }}>
        {icon} {label}
      </span>
    </div>
  )
}

function MotmRadio({ active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={active ? 'Homme du match' : 'Élire homme du match'}
      aria-pressed={active}
      style={{
        width: 30, height: 30, flexShrink: 0, padding: 0, cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%',
        background: active ? 'rgba(245,215,110,0.18)' : 'transparent',
        border: `1px solid ${active ? GOLDB : RULE}`,
      }}
    >
      <Star size={15} color={active ? GOLDB : MUTED} fill={active ? GOLDB : 'none'} />
    </button>
  )
}

export default function SendCardsModal({ isOpen, onClose, matchId, matchInfo, lineup = [], onSuccess, onPlayerUpdated }) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768)
  const [viewMode, setViewMode] = useState('quick') // quick | detailed
  const [selectedPlayerIds, setSelectedPlayerIds] = useState([])
  const [ratings, setRatings] = useState({})
  const [tags, setTags] = useState({})
  const [stats, setStats] = useState({}) // { pid: { goals, assists, minutes } } (strings)
  const [motmId, setMotmId] = useState(null)
  const [privateMessage, setPrivateMessage] = useState('')
  const [expandedId, setExpandedId] = useState(null) // mobile : ligne deployee
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [editingEmailFor, setEditingEmailFor] = useState(null)
  const [emailError, setEmailError] = useState(null)
  const [savingEmail, setSavingEmail] = useState(false)
  const [editingPhoneFor, setEditingPhoneFor] = useState(null)
  const [phoneError, setPhoneError] = useState(null)
  const [savingPhone, setSavingPhone] = useState(false)
  const [shareData, setShareData] = useState(null)
  const [lineupOverrides, setLineupOverrides] = useState({})

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  // Reset a chaque ouverture : tout joueur joignable (email OU phone)
  // pre-coche (PR #20). Sans contact -> decoche (le backend le rejette).
  useEffect(() => {
    if (!isOpen) return
    setSelectedPlayerIds(lineup.filter(p => p.email || isValidPhone(p.phone)).map(p => p.id))
    setViewMode('quick')
    setRatings({})
    setTags({})
    setStats({})
    setMotmId(null)
    setPrivateMessage('')
    setExpandedId(null)
    setError(null)
    setSubmitting(false)
    setEditingEmailFor(null)
    setEmailError(null)
    setSavingEmail(false)
    setEditingPhoneFor(null)
    setPhoneError(null)
    setSavingPhone(false)
    setLineupOverrides({})
  }, [isOpen, lineup])

  useEffect(() => { if (!isOpen) setShareData(null) }, [isOpen])

  const enrichedLineup = useMemo(
    () => lineup.map(p => ({ ...p, ...(lineupOverrides[p.id] || {}) })),
    [lineup, lineupOverrides]
  )

  const selectedCount = selectedPlayerIds.length
  const allSelected = lineup.length > 0 && selectedCount === lineup.length
  const score = useMemo(() => {
    // Oriente club (le notre d'abord) : l'en-tete se lit « Pau FC · 0-2 ».
    // Le parent fournit scoreUs/scoreThem deja orientes.
    const us = matchInfo?.scoreUs
    const them = matchInfo?.scoreThem
    return (us != null && them != null) ? `${us}-${them}` : ''
  }, [matchInfo])

  // Recap : nom du joueur MOTM (seulement s'il est coche).
  const motmName = useMemo(() => {
    if (!motmId || !selectedPlayerIds.includes(motmId)) return null
    const p = enrichedLineup.find(x => x.id === motmId)
    return p ? p.name : null
  }, [motmId, selectedPlayerIds, enrichedLineup])

  const noContactCount = useMemo(
    () => selectedPlayerIds.filter(pid => {
      const p = enrichedLineup.find(x => x.id === pid)
      return p && !p.email && !isValidPhone(p.phone)
    }).length,
    [selectedPlayerIds, enrichedLineup]
  )

  if (!isOpen) return null

  if (shareData) {
    const resolveName = (pid) => {
      const p = enrichedLineup.find(x => x.id === pid)
      return { player_id: pid, name: p?.name || pid }
    }

    async function handleForceResend(playerIds) {
      setSubmitting(true)
      setError(null)
      const payload = {
        players: playerIds.map(pid => {
          const r = ratings[pid]
          const s = stats[pid] || {}
          return {
            player_id: pid,
            coach_rating: (r && r > 0) ? r : null,
            identity_tag: tags[pid] || null,
            goals: parseStat(s.goals, 20),
            assists: parseStat(s.assists, 20),
            minutes_played: parseStat(s.minutes, 120),
          }
        }),
        private_message: privateMessage.trim() || null,
        homme_du_match_player_id: null,
        force_resend_ids: playerIds,
      }
      try {
        const res = await fetch(`${API}/matches/${matchId}/send-all-cards/`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify(payload),
        })
        const data = await safeJson(res)
        if (!res.ok) {
          const detail = data?.detail
          throw new Error(typeof detail === 'string' ? detail : 'Erreur lors du renvoi')
        }
        setShareData(prev => ({
          ...prev,
          cards: [...(prev.cards || []), ...(data.cards || [])],
          skipped_already_downloaded: (prev.skipped_already_downloaded || []).filter(id => !playerIds.includes(id)),
          cards_processed: (prev.cards_processed || 0) + (data.cards_processed || 0),
        }))
      } catch (err) {
        setError(err.message || 'Une erreur est survenue lors du renvoi')
      } finally {
        setSubmitting(false)
      }
    }

    return (
      <ShareWhatsAppModal
        isOpen
        cards={shareData.cards || []}
        skippedAlreadyDownloaded={(shareData.skipped_already_downloaded || []).map(resolveName)}
        skippedNoContact={(shareData.skipped_no_contact || []).map(resolveName)}
        skippedNotInMatch={(shareData.skipped_not_in_match || []).map(resolveName)}
        matchInfo={matchInfo}
        onPlayerUpdated={onPlayerUpdated}
        onBack={() => setShareData(null)}
        onForceResend={handleForceResend}
        forceResendBusy={submitting}
        forceResendError={error}
        onClose={() => { setShareData(null); onClose() }}
      />
    )
  }

  const toggleSelection = (pid) => {
    setSelectedPlayerIds(prev => {
      const next = prev.includes(pid) ? prev.filter(x => x !== pid) : [...prev, pid]
      // MOTM doit faire partie des coches : decoche le joueur -> clear MOTM.
      if (!next.includes(pid) && motmId === pid) setMotmId(null)
      return next
    })
  }
  const toggleAll = () => setSelectedPlayerIds(allSelected ? [] : lineup.map(p => p.id))
  const setRating = (pid, val) => setRatings(prev => ({ ...prev, [pid]: val }))
  const setTag = (pid, tag) => setTags(prev => ({ ...prev, [pid]: tag }))
  const setStat = (pid, key, val) => setStats(prev => ({ ...prev, [pid]: { ...(prev[pid] || {}), [key]: val } }))
  const toggleMotm = (pid) => {
    // Un seul elu. Reclic = retire. Auto-coche le joueur si besoin.
    setMotmId(cur => (cur === pid ? null : pid))
    setSelectedPlayerIds(prev => (prev.includes(pid) ? prev : [...prev, pid]))
  }

  // "Tout à 90'" : remplit minutes pour les coches sans valeur saisie.
  const fillAll90 = () => {
    setStats(prev => {
      const next = { ...prev }
      selectedPlayerIds.forEach(pid => {
        const cur = next[pid]?.minutes
        if (cur == null || String(cur).trim() === '') {
          next[pid] = { ...(next[pid] || {}), minutes: '90' }
        }
      })
      return next
    })
  }

  function handleCancelEmail() { setEditingEmailFor(null); setEmailError(null) }
  function handleCancelPhone() { setEditingPhoneFor(null); setPhoneError(null) }

  async function handleSavePhone(p, value) {
    const phone = (value || '').replace(/[\s.]/g, '')
    if (!isValidPhone(phone)) { setPhoneError('Numéro invalide (06…)'); return }
    setSavingPhone(true); setPhoneError(null)
    try {
      await playerService.updatePlayer(p.id, { phone })
      setLineupOverrides(prev => ({ ...prev, [p.id]: { ...(prev[p.id] || {}), phone } }))
      onPlayerUpdated && onPlayerUpdated(p.id, { phone })
      setSelectedPlayerIds(prev => prev.includes(p.id) ? prev : [...prev, p.id])
      setEditingPhoneFor(null)
    } catch (err) {
      setPhoneError(err?.response?.data?.detail || err?.message || 'Erreur sauvegarde téléphone')
    } finally { setSavingPhone(false) }
  }

  async function handleSaveEmail(p, value) {
    const email = (value || '').trim()
    if (!isValidEmail(email)) { setEmailError('Email invalide'); return }
    setSavingEmail(true); setEmailError(null)
    try {
      await playerService.updatePlayer(p.id, { email })
      setLineupOverrides(prev => ({ ...prev, [p.id]: { ...(prev[p.id] || {}), email } }))
      onPlayerUpdated && onPlayerUpdated(p.id, { email })
      setSelectedPlayerIds(prev => prev.includes(p.id) ? prev : [...prev, p.id])
      setEditingEmailFor(null)
    } catch (err) {
      setEmailError(err?.response?.data?.detail || err?.message || 'Erreur sauvegarde email')
    } finally { setSavingEmail(false) }
  }

  async function handleSubmit() {
    setSubmitting(true)
    setError(null)
    const payload = {
      players: selectedPlayerIds.map(pid => {
        const r = ratings[pid]
        const s = stats[pid] || {}
        return {
          player_id: pid,
          coach_rating: (r && r > 0) ? r : null,
          identity_tag: tags[pid] || null,
          goals: parseStat(s.goals, 20),
          assists: parseStat(s.assists, 20),
          minutes_played: parseStat(s.minutes, 120),
        }
      }),
      private_message: privateMessage.trim() || null,
      homme_du_match_player_id: (motmId && selectedPlayerIds.includes(motmId)) ? motmId : null,
    }
    try {
      const res = await fetch(`${API}/matches/${matchId}/send-all-cards/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      })
      const data = await safeJson(res)
      if (!res.ok) {
        const detail = data?.detail
        throw new Error(typeof detail === 'string' ? detail : "Erreur lors de l'envoi des cartes")
      }
      onSuccess && onSuccess(data)
      setShareData(data)
    } catch (err) {
      setError(err.message || 'Une erreur est survenue')
    } finally {
      setSubmitting(false)
    }
  }

  // ── Sous-rendus ──────────────────────────────────────────────────────
  const contactRow = (p) => {
    const editing = editingEmailFor === p.id
    const editingPhone = editingPhoneFor === p.id
    const hasPhone = isValidPhone(p.phone)
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div>
          {editing ? (
            <EmailEdit initial={p.email || ''} saving={savingEmail} error={emailError}
              onSave={(val) => handleSaveEmail(p, val)} onCancel={handleCancelEmail} />
          ) : p.email ? (
            <span title={p.email} style={{ display: 'inline-block', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: MONO, fontSize: 11, color: 'rgba(120,200,120,0.9)' }}>
              ✓ {p.email}
            </span>
          ) : (
            <button type="button" onClick={() => { setEmailError(null); setEditingEmailFor(p.id) }}
              style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: GOLD, fontFamily: MONO, fontSize: 11, textDecoration: 'underline', textDecorationStyle: 'dotted' }}>
              📧 Ajouter un email
            </button>
          )}
        </div>
        <div>
          {editingPhone ? (
            <PhoneEdit initial={p.phone || ''} saving={savingPhone} error={phoneError}
              onSave={(val) => handleSavePhone(p, val)} onCancel={handleCancelPhone} />
          ) : hasPhone ? (
            <span title={p.phone} style={{ fontFamily: MONO, fontSize: 11, color: 'rgba(120,200,120,0.9)' }}>📱 {p.phone}</span>
          ) : (
            <button type="button" onClick={() => { setPhoneError(null); setEditingPhoneFor(p.id) }}
              style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: GOLD, fontFamily: MONO, fontSize: 11, textDecoration: 'underline', textDecorationStyle: 'dotted' }}>
              📱 Ajouter un téléphone
            </button>
          )}
        </div>
      </div>
    )
  }

  const detailedBlock = (p) => {
    const s = stats[p.id] || {}
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
          <div>
            <label style={{ display: 'block', fontFamily: MONO, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', color: MUTED, marginBottom: 8 }}>Note coach (privée)</label>
            <RatingSlider large value={ratings[p.id] ?? 0} onChange={(v) => setRating(p.id, v)} />
          </div>
          <div>
            <label style={{ display: 'block', fontFamily: MONO, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', color: MUTED, marginBottom: 8 }}>Tag identitaire</label>
            <TagPicker value={tags[p.id] || null} onChange={(tag) => setTag(p.id, tag)} />
          </div>
        </div>
        <div>
          <label style={{ display: 'block', fontFamily: MONO, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', color: MUTED, marginBottom: 8 }}>Stats du match</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <StatInput large icon="⚽" label="Buts" max={20} value={s.goals} onChange={(v) => setStat(p.id, 'goals', v)} />
            <StatInput large icon="🎯" label="Passes" max={20} value={s.assists} onChange={(v) => setStat(p.id, 'assists', v)} />
            <StatInput large icon="⏱️" label="Minutes" max={120} value={s.minutes} onChange={(v) => setStat(p.id, 'minutes', v)} />
          </div>
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          padding: '10px 12px', background: motmId === p.id ? 'rgba(245,215,110,0.08)' : 'rgba(245,242,235,0.03)',
          border: `1px solid ${motmId === p.id ? 'rgba(245,215,110,0.4)' : RULE}`,
        }}>
          <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase', color: motmId === p.id ? GOLDB : MUTED }}>
            ★ Homme du match
          </span>
          <MotmRadio active={motmId === p.id} onClick={() => toggleMotm(p.id)} />
        </div>
      </div>
    )
  }

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose() }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(5,4,4,0.8)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'center',
        padding: isMobile ? 0 : 16,
      }}
    >
      <div style={{
        background: D, color: CREAM, fontFamily: BODY,
        width: '100%', maxWidth: isMobile ? '100%' : 760,
        height: isMobile ? '100dvh' : 'min(88vh, 820px)',
        display: 'flex', flexDirection: 'column',
        border: isMobile ? 'none' : `1px solid ${RULE}`,
        borderTop: isMobile ? 'none' : `2px solid ${GOLD}`,
      }}>
        {/* Header sticky */}
        <header style={{ flexShrink: 0, padding: '14px 18px', background: PANEL, borderBottom: `1px solid ${RULE}` }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.18em', textTransform: 'uppercase', color: GOLD, marginBottom: 4 }}>
                Cartes joueurs
              </div>
              <h2 style={{ fontFamily: DISP, fontSize: isMobile ? 18 : 22, textTransform: 'uppercase', lineHeight: 1, color: CREAM, margin: 0, letterSpacing: '.01em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                Envoyer les cartes
              </h2>
              <div style={{ fontFamily: MONO, fontSize: 10, color: MUTED, marginTop: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {[matchInfo?.opponentName, score, matchInfo?.date].filter(Boolean).join('  ·  ') || 'Match'}
              </div>
            </div>
            <button type="button" onClick={() => !submitting && onClose()} aria-label="Fermer"
              style={{ flexShrink: 0, background: 'none', border: `1px solid ${RULE}`, padding: 6, cursor: 'pointer' }}>
              <X size={14} color={MUTED} />
            </button>
          </div>
          {/* Toggle Rapide / Détaillé */}
          <div style={{ display: 'inline-flex', marginTop: 12, border: `1px solid ${RULE}` }}>
            {[['quick', 'Mode rapide'], ['detailed', 'Mode détaillé']].map(([m, lbl]) => (
              <button key={m} type="button" onClick={() => setViewMode(m)}
                style={{
                  padding: '7px 14px', cursor: 'pointer', border: 'none',
                  background: viewMode === m ? GOLD : 'transparent',
                  color: viewMode === m ? D : MUTED,
                  fontFamily: MONO, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', fontWeight: 700,
                }}>
                {lbl}
              </button>
            ))}
          </div>
        </header>

        {/* Récap sticky */}
        <div style={{
          flexShrink: 0, padding: '10px 18px', background: 'rgba(79,142,247,0.06)',
          borderBottom: `1px solid ${RULE}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
        }}>
          <div style={{ fontFamily: MONO, fontSize: 10, color: CREAM, lineHeight: 1.6 }}>
            <strong style={{ color: GOLD }}>{selectedCount}</strong> sélectionné{selectedCount > 1 ? 's' : ''}
            {'  ·  '}★ {motmName ? motmName : <span style={{ color: MUTED }}>aucun homme du match</span>}
            {'  ·  '}📩 {privateMessage.trim() ? 'message défini' : <span style={{ color: MUTED }}>pas de message</span>}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={toggleAll}
              style={{ padding: '6px 10px', cursor: 'pointer', background: 'transparent', border: `1px solid ${RULE}`, color: CREAM, fontFamily: MONO, fontSize: 9, letterSpacing: '.06em', textTransform: 'uppercase' }}>
              {allSelected ? 'Aucun' : 'Tout'}
            </button>
            <button type="button" onClick={fillAll90} disabled={selectedCount === 0}
              style={{ padding: '6px 10px', cursor: selectedCount === 0 ? 'not-allowed' : 'pointer', background: 'transparent', border: `1px solid ${GOLD}`, color: selectedCount === 0 ? MUTED : GOLD, fontFamily: MONO, fontSize: 9, letterSpacing: '.06em', textTransform: 'uppercase' }}>
              ⏱️ Tout à 90'
            </button>
          </div>
        </div>

        {/* Liste scrollable */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {/* Message global */}
          <div style={{ padding: '14px 18px 8px' }}>
            <textarea
              value={privateMessage}
              onChange={e => setPrivateMessage(e.target.value.slice(0, 500))}
              maxLength={500} rows={2}
              placeholder="Un mot pour toute l'équipe (optionnel) : fierté, prochain rendez-vous, bravo collectif..."
              style={{ width: '100%', resize: 'vertical', padding: '10px 12px', background: 'rgba(245,242,235,0.04)', border: `1px solid ${RULE}`, fontFamily: BODY, fontSize: 13, color: CREAM, outline: 'none' }}
            />
            <div style={{ fontFamily: MONO, fontSize: 9, color: MUTED, textAlign: 'right', marginTop: 2 }}>{privateMessage.length}/500</div>
          </div>

          {lineup.length === 0 && (
            <div style={{ padding: 40, textAlign: 'center', fontFamily: MONO, fontSize: 12, color: MUTED }}>
              Aucun joueur dans la composition.
            </div>
          )}

          {enrichedLineup.map((p, idx) => {
            const hasEmail = !!p.email
            const hasPhone = isValidPhone(p.phone)
            const hasContact = hasEmail || hasPhone
            const checked = selectedPlayerIds.includes(p.id)
            const isMotm = motmId === p.id
            const s = stats[p.id] || {}
            const last = idx === enrichedLineup.length - 1
            const expanded = expandedId === p.id

            // ── Mobile : ligne compacte + expand inline ──────────────────
            if (isMobile) {
              return (
                <div key={p.id} style={{
                  borderBottom: last ? 'none' : `1px solid ${RULE}`,
                  borderLeft: isMotm ? `3px solid ${GOLDB}` : '3px solid transparent',
                  background: isMotm ? 'rgba(245,215,110,0.05)' : 'transparent',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px' }}>
                    <CheckBox checked={checked} hasContact={hasContact} onChange={() => toggleSelection(p.id)} />
                    <PlayerAvatar photoUrl={p.photo_url} name={p.name} />
                    <button type="button" onClick={() => setExpandedId(expanded ? null : p.id)}
                      style={{ flex: 1, minWidth: 0, textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                      <div style={{ fontFamily: BODY, fontSize: 14, fontWeight: 700, color: CREAM, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.number != null ? `${p.number}. ` : ''}{p.name}
                      </div>
                      <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.06em', textTransform: 'uppercase', color: hasContact ? MUTED : RED, marginTop: 3 }}>
                        {(p.position || 'Joueur')}{!hasContact ? ' · sans contact' : ''}
                        {ratings[p.id] > 0 ? ` · ${ratings[p.id]}/10` : ''}{tags[p.id] ? ` · ${tags[p.id].emoji}` : ''}
                      </div>
                    </button>
                    {isMotm && <Star size={14} color={GOLDB} fill={GOLDB} style={{ flexShrink: 0 }} />}
                    <ChevronDown size={16} color={MUTED} style={{ flexShrink: 0, transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}
                      onClick={() => setExpandedId(expanded ? null : p.id)} />
                  </div>
                  {expanded && (
                    <div style={{ padding: '0 18px 16px' }}>
                      {contactRow(p)}
                      {detailedBlock(p)}
                    </div>
                  )}
                </div>
              )
            }

            // ── Détaillé : bloc complet ──────────────────────────────────
            if (viewMode === 'detailed') {
              return (
                <div key={p.id} style={{
                  padding: '16px 18px',
                  borderBottom: last ? 'none' : `1px solid ${RULE}`,
                  borderLeft: isMotm ? `3px solid ${GOLDB}` : '3px solid transparent',
                  background: isMotm ? 'rgba(245,215,110,0.05)' : 'transparent',
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                    <PlayerAvatar photoUrl={p.photo_url} name={p.name} size={56} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: BODY, fontSize: 17, fontWeight: 700, color: CREAM }}>
                        {p.number != null ? `${p.number}. ` : ''}{p.name}
                      </div>
                      <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.06em', textTransform: 'uppercase', color: MUTED, margin: '4px 0 8px' }}>
                        {p.position || 'Joueur'}
                      </div>
                      {contactRow(p)}
                    </div>
                    <CheckBox checked={checked} hasContact={hasContact} onChange={() => toggleSelection(p.id)} />
                  </div>
                  {detailedBlock(p)}
                </div>
              )
            }

            // ── Rapide : ligne en grille ─────────────────────────────────
            return (
              <div key={p.id} style={{
                display: 'grid',
                gridTemplateColumns: '26px 36px minmax(130px,1fr) 150px 124px 132px 34px',
                gap: 10, alignItems: 'center',
                padding: '12px 18px',
                borderBottom: last ? 'none' : `1px solid ${RULE}`,
                borderLeft: isMotm ? `3px solid ${GOLDB}` : '3px solid transparent',
                background: isMotm ? 'rgba(245,215,110,0.05)' : 'transparent',
              }}>
                <CheckBox checked={checked} hasContact={hasContact} onChange={() => toggleSelection(p.id)} />
                <PlayerAvatar photoUrl={p.photo_url} name={p.name} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontFamily: BODY, fontSize: 14, fontWeight: 700, color: CREAM, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.number != null ? `${p.number}. ` : ''}{p.name}
                  </div>
                  <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.06em', textTransform: 'uppercase', color: MUTED, marginTop: 3 }}>
                    {p.position || 'Joueur'}
                  </div>
                  {!hasContact && (
                    <button type="button" onClick={() => { setEmailError(null); setEditingEmailFor(p.id) }}
                      style={{ background: 'none', border: 'none', padding: 0, marginTop: 4, cursor: 'pointer', color: RED, fontFamily: MONO, fontSize: 9, textDecoration: 'underline', textDecorationStyle: 'dotted' }}>
                      contact manquant
                    </button>
                  )}
                  {editingEmailFor === p.id && (
                    <div style={{ marginTop: 4 }}>
                      <EmailEdit initial={p.email || ''} saving={savingEmail} error={emailError}
                        onSave={(val) => handleSaveEmail(p, val)} onCancel={handleCancelEmail} />
                    </div>
                  )}
                </div>
                <RatingSlider value={ratings[p.id] ?? 0} onChange={(v) => setRating(p.id, v)} />
                <TagPicker compact value={tags[p.id] || null} onChange={(tag) => setTag(p.id, tag)} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                  <StatInput icon="⚽" label="B" max={20} value={s.goals} onChange={(v) => setStat(p.id, 'goals', v)} />
                  <StatInput icon="🎯" label="P" max={20} value={s.assists} onChange={(v) => setStat(p.id, 'assists', v)} />
                  <StatInput icon="⏱️" label="Min" max={120} value={s.minutes} onChange={(v) => setStat(p.id, 'minutes', v)} />
                </div>
                <MotmRadio active={isMotm} onClick={() => toggleMotm(p.id)} />
              </div>
            )
          })}
        </div>

        {/* Footer sticky */}
        <footer style={{ flexShrink: 0, padding: '12px 18px', background: PANEL, borderTop: `1px solid ${RULE}`, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {error && (
            <div style={{ fontFamily: MONO, fontSize: 11, color: '#fff', background: 'rgba(239,68,68,0.18)', border: `1px solid ${RED}`, padding: '8px 10px' }}>
              {error}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0, fontFamily: MONO, fontSize: 10, color: MUTED, lineHeight: 1.5 }}>
              <strong style={{ color: CREAM }}>{selectedCount}</strong> carte{selectedCount > 1 ? 's' : ''} prête{selectedCount > 1 ? 's' : ''}
              {noContactCount > 0 && <span style={{ color: RED }}> · {noContactCount} sans contact</span>}
            </div>
            <button type="button" onClick={onClose} disabled={submitting}
              style={{ flex: '0 0 auto', padding: '11px 18px', background: 'transparent', border: `1px solid ${RULE}`, color: MUTED, fontFamily: MONO, fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase', cursor: submitting ? 'not-allowed' : 'pointer' }}>
              Annuler
            </button>
            <button type="button" onClick={handleSubmit} disabled={submitting || selectedCount === 0}
              style={{
                flex: '0 0 auto', minWidth: 160, padding: '11px 22px',
                background: (submitting || selectedCount === 0) ? 'rgba(79,142,247,0.3)' : GOLD,
                color: (submitting || selectedCount === 0) ? MUTED : D,
                border: 'none', fontFamily: MONO, fontSize: 11, letterSpacing: '.12em', textTransform: 'uppercase', fontWeight: 700,
                cursor: (submitting || selectedCount === 0) ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}>
              {submitting ? 'Envoi...' : <>Envoyer ({selectedCount})</>}
            </button>
          </div>
        </footer>
      </div>
    </div>
  )
}
